// GA4 facet filter tracking via dataLayer.push (GTM) — news, events, and site-wide search surfaces
const SEARCH_SURFACE_SELECTOR = '[data-search-surface]'
const SEARCH_INPUT_SELECTOR = '[data-track-search-input="true"]'
const FACET_CONTROL_SELECTOR = '[data-track-facet-control="true"]'
const RESULT_LINK_SELECTOR = '[data-track-result-click="true"]'
const DEBOUNCE_MS = 650

let searchQueryTimer = null
let searchQueryFallbackTimer = null
let pendingSearchQuery = null
let resultsViewTimer = null
let pendingFacetEvents = []
let facetFallbackTimer = null
let lastResultsViewSignature = null

function pushDataLayerEvent(payload) {
	window.dataLayer = window.dataLayer || []
	window.dataLayer.push(payload)
}

function parseCount(value) {
	const parsed = Number.parseInt(value ?? '', 10)
	return Number.isNaN(parsed) ? 0 : parsed
}

function getTotalResultsCount(surface) {
	if (!surface) return 0

	// Prefer a live descendant carrying its own count (e.g. the Sprig-swapped
	// results node) over the surface's own attribute, which may be stale if
	// the surface element itself is never replaced by a partial swap.
	const liveCountEl = surface.querySelector('[data-total-results-count]')
	if (liveCountEl) {
		return parseCount(liveCountEl.dataset.totalResultsCount)
	}

	return parseCount(surface.dataset.resultsCount)
}

function getLiveSearchTerm(surface) {
	// If the surface contains its own live keyword input (e.g. the header
	// autocomplete, where the input itself is never swapped), its current
	// value is always accurate — reading it avoids relying on a
	// data-search-term attribute on an ancestor that Sprig may never re-render.
	const liveInput = surface.querySelector(SEARCH_INPUT_SELECTOR)
	if (liveInput) return liveInput.value ?? ''

	return surface.dataset.searchTerm ?? ''
}

function getLiveActiveFacets(surface) {
	// Same rationale as getTotalResultsCount: a facet-only swap (e.g. the
	// search page's #results fragment) never touches the outer surface
	// wrapper's own attributes, so a live descendant's value (when present)
	// is preferred over the surface's own, possibly stale, attribute.
	const liveEl = surface.querySelector('[data-active-facets]')
	if (liveEl) return liveEl.dataset.activeFacets ?? ''

	return surface.dataset.activeFacets ?? ''
}

function getSurfaceState(surface) {
	if (!surface) {
		return { searchSurface: '', searchTerm: '', activeFacets: '', resultsCount: 0, page: '1' }
	}

	return {
		searchSurface: surface.dataset.searchSurface ?? '',
		searchTerm: getLiveSearchTerm(surface),
		activeFacets: getLiveActiveFacets(surface),
		resultsCount: getTotalResultsCount(surface),
		page: surface.dataset.resultsPage ?? '1',
	}
}

function flushPendingSearchQuery() {
	if (!pendingSearchQuery) return

	const surface = pendingSearchQuery.surface
	if (!surface) {
		pendingSearchQuery = null
		return
	}

	const state = getSurfaceState(surface)

	// Wait for Sprig state to catch up to the debounced input value.
	if (state.searchTerm !== pendingSearchQuery.term) return

	pushDataLayerEvent({
		event: 'search_query',
		search_surface: state.searchSurface,
		search_term: state.searchTerm,
		results_count: state.resultsCount,
	})

	pendingSearchQuery = null
	window.clearTimeout(searchQueryFallbackTimer)
}

function queueSearchQuery(term, surface) {
	pendingSearchQuery = { term: term ?? '', surface }

	window.clearTimeout(searchQueryFallbackTimer)
	// Last-resort safety net only, for the rare case htmx:afterSettle never
	// fires at all (e.g. a failed request). Sprig round-trips routinely take
	// over a second under normal (and especially local dev) conditions, so
	// this must stay well above that or it wins the race against the real
	// event and flushes stale pre-response state — which is exactly what
	// empirical testing caught it doing at a 1500ms delay.
	searchQueryFallbackTimer = window.setTimeout(() => {
		if (!pendingSearchQuery || !pendingSearchQuery.surface) return

		const state = getSurfaceState(pendingSearchQuery.surface)
		pushDataLayerEvent({
			event: 'search_query',
			search_surface: state.searchSurface,
			search_term: pendingSearchQuery.term,
			results_count: state.resultsCount,
		})
		pendingSearchQuery = null
	}, 6000)
}

function queueFacetEvent(meta) {
	pendingFacetEvents.push(meta)

	window.clearTimeout(facetFallbackTimer)
	// Last-resort safety net only — see queueSearchQuery's comment. The real
	// flush happens via htmx:afterSettle; this must not fire before that
	// normally does, or it pushes facet_apply with the pre-click state.
	facetFallbackTimer = window.setTimeout(() => {
		flushFacetEvents()
	}, 6000)
}

function flushFacetEvents() {
	if (!pendingFacetEvents.length) return

	const eventsToFlush = pendingFacetEvents
	pendingFacetEvents = []

	eventsToFlush.forEach((meta) => {
		const state = getSurfaceState(meta.surface)
		pushDataLayerEvent({
			event: 'facet_apply',
			search_surface: state.searchSurface,
			facet_group: meta.facetGroup,
			facet_value: meta.facetValue,
			facet_action: meta.facetAction,
			search_term: state.searchTerm,
			active_facets: state.activeFacets,
			results_count: state.resultsCount,
		})
	})
}

function queueResultsViewEvent(surface) {
	window.clearTimeout(resultsViewTimer)
	resultsViewTimer = window.setTimeout(() => {
		// No page-wide fallback here: both call sites (the init loop and
		// handleAfterSettle) already resolve a specific, non-passive surface
		// themselves before calling this — a blind document-wide query here
		// would just as easily re-pick a passive surface (e.g. the header
		// autocomplete) as the caller's real target, which is exactly the
		// phantom-event bug this replaced.
		if (!surface) return

		const state = getSurfaceState(surface)
		const signature = [state.searchSurface, state.searchTerm, state.activeFacets, state.resultsCount, state.page].join(
			'||',
		)

		// Prevent duplicate search_results_view pushes for unchanged state.
		if (signature === lastResultsViewSignature) return
		lastResultsViewSignature = signature

		pushDataLayerEvent({
			event: 'search_results_view',
			search_surface: state.searchSurface,
			search_term: state.searchTerm,
			active_facets: state.activeFacets,
			results_count: state.resultsCount,
		})
	}, DEBOUNCE_MS)
}

function handleKeywordInput(event) {
	const input = event.target.closest(SEARCH_INPUT_SELECTOR)
	if (!input) return

	const surface = input.closest(SEARCH_SURFACE_SELECTOR)
	if (!surface) return

	window.clearTimeout(searchQueryTimer)
	searchQueryTimer = window.setTimeout(() => {
		// Queue only — don't flush here. Now that searchTerm can be read live
		// from the input itself (see getLiveSearchTerm), the term-match gate in
		// flushPendingSearchQuery would pass instantly, before Sprig's response
		// (and its up-to-date results_count) has actually landed. Let the
		// sprig:render listener (or the fallback timer) do the flushing once
		// the real result count is available.
		queueSearchQuery(input.value ?? '', surface)
	}, DEBOUNCE_MS)
}

function facetMetaFromElement(element) {
	return {
		facetGroup: element.dataset.facetGroup ?? '',
		facetValue: element.dataset.facetValue ?? element.value ?? '',
		facetAction: element.dataset.facetAction ?? 'add',
		surface: element.closest(SEARCH_SURFACE_SELECTOR),
	}
}

function handleFacetInputChange(event) {
	const control = event.target.closest(`input${FACET_CONTROL_SELECTOR}`)
	if (!control) return
	queueFacetEvent(facetMetaFromElement(control))
}

function handleFacetRemoveClick(event) {
	const control = event.target.closest(`button${FACET_CONTROL_SELECTOR}`)
	if (!control) return
	queueFacetEvent(facetMetaFromElement(control))
}

function handleResultClick(event) {
	const link = event.target.closest(`a${RESULT_LINK_SELECTOR}`)
	if (!link) return

	const surface = link.closest(SEARCH_SURFACE_SELECTOR)
	if (!surface) return

	// Pushed immediately and synchronously, unlike the queued/debounced facet
	// events above — this click is about to navigate the browser away, so
	// there's no Sprig response to wait for and no reason to delay. Reads
	// whatever state is already settled on the surface at the moment of click.
	const state = getSurfaceState(surface)
	pushDataLayerEvent({
		event: 'search_result_click',
		search_surface: state.searchSurface,
		search_term: state.searchTerm,
		active_facets: state.activeFacets,
		results_count: state.resultsCount,
		result_type: link.dataset.resultType ?? '',
		result_position: parseCount(link.dataset.resultPosition),
	})
}

function handleAfterSettle(event) {
	flushPendingSearchQuery()
	flushFacetEvents()

	// 'sprig:' is only a PHP-side request-parameter prefix in this plugin
	// (sprig:config, sprig:action, etc.) — there is no 'sprig:render' DOM
	// event. Sprig is built on HTMX, which dispatches real 'htmx:afterSettle'
	// events once swapped content has landed in the DOM *and* settled.
	// 'htmx:afterSwap' fires earlier — for an hx-select swap specifically,
	// state reads (data-total-results-count, data-active-facets) inside an
	// afterSwap handler were empirically observed still returning the
	// pre-swap values, so afterSettle is the correct, safe point to read from.
	// For an hx-select swap, event.target/detail.target can still reference
	// the node as it existed in htmx's off-document parsed response at the
	// moment this fires, not the node actually attached to the live page —
	// .closest() on that reference returns null even though the swap has
	// already landed. Re-resolving by id against the live document sidesteps
	// this; only fall back to the raw reference for un-identified targets.
	const rawTarget = event?.detail?.target ?? event?.target
	const target = (rawTarget?.id && document.getElementById(rawTarget.id)) || rawTarget
	let surface = target?.closest?.(SEARCH_SURFACE_SELECTOR) ?? null
	if (!surface) {
		// Only fall back to a page-wide guess when the swap's own target can't
		// resolve one, and never guess a passive surface (e.g. the header
		// autocomplete) — that would misattribute an unrelated swap's
		// results-view to a surface the user never actually interacted with.
		const fallback = document.querySelector(SEARCH_SURFACE_SELECTOR)
		surface = fallback && fallback.dataset.searchPassive !== 'true' ? fallback : null
	}
	queueResultsViewEvent(surface)
}

function initFacetTracking() {
	if (!document.querySelector(SEARCH_SURFACE_SELECTOR)) return

	document.addEventListener('input', handleKeywordInput)
	document.addEventListener('change', handleFacetInputChange, true)
	document.addEventListener('click', handleFacetRemoveClick, true)
	document.addEventListener('click', handleResultClick, true)
	document.addEventListener('htmx:afterSettle', handleAfterSettle)

	// Fire an initial results-view for every surface present at load, except
	// ones marked passive (e.g. the header autocomplete, which starts empty
	// and idle on every single page — nothing meaningful to report until the
	// user actually interacts with it). Without this exclusion, a page like
	// /search that has both the real results surface *and* the ambient
	// header widget would resolve to whichever happens to sit first in the
	// DOM, misattributing the page's own initial view to the wrong surface.
	document.querySelectorAll(SEARCH_SURFACE_SELECTOR).forEach((surface) => {
		if (surface.dataset.searchPassive === 'true') return
		queueResultsViewEvent(surface)
	})
}

initFacetTracking()
